import { BaseService } from '../base';

export type UserProfile =
  | 'Administrador'
  | 'CX Atendimento'
  | 'Subscritor'
  | 'Gerente Comercial'
  | 'Executivo de Vendas'
  | 'Assistente Comercial'
  | 'Master'
  | 'Assessor'
  | 'Corretor';

export interface User {
  id: string;
  name: string;
  email: string;
  status: 'FORCE_CHANGE_PASSWORD' | 'INACTIVE' | null;
  role: 'admin' | 'assessoria' | 'membro' | 'gerente';
  groupName: string;
  identityId?: string;
  recordId?: string;
  recordUsage?: string;
  partyCode?: string;
  registerNumber?: string;
  phone?: string;
  profile?: UserProfile;
  deactivated_at?: string | null;
  partyId?: string;
  advisoryId?: string;
  customAttributes?: {
    'custom:group'?: string;
    'custom:contact_number'?: string;
    'custom:register_number'?: string;
    'custom:profile'?: string;
  };
}

export interface RegisterUserPayload {
  email: string;
  password?: string;
  name: string;
  partyId?: string;
  customAttributes: {
    'custom:group': string;
    'custom:contact_number'?: string;
    'custom:register_number'?: string;
    'custom:profile'?: string;
    'custom:party_type'?: string;
    'custom:party_id'?: string;
    'custom:advisory_id'?: string;
  };
}

export interface EditUserPayload {
  email: string;
  name: string;
  /** Corretora vinculada (PartyCode). Só Seg Adm/CX trocam; a Api barra os demais. */
  partyId?: string;
  customAttributes: {
    'custom:group'?: string;
    'custom:contact_number'?: string;
    'custom:register_number'?: string;
    'custom:profile'?: string;
  };
  recordId?: string;
  identityId?: string;
  recordUsage?: string;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  id?: string;
  partyCode?: string;
}

export type UsersReportStatus = 'FORCE_CHANGE_PASSWORD' | 'ACTIVE' | 'INACTIVE';

export type UsersReportScope = 'insurer' | 'broker' | 'advisory';

export interface UsersReportRow {
  identityId: string;
  name: string;
  email: string;
  registerNumber: string;
  phone: string;
  groupName: string;
  scope: UsersReportScope | 'unknown';
  status: UsersReportStatus;
  cognitoStatus: string;
  cognitoEnabled: boolean;
  brokerCode: string;
  brokerName: string;
  advisoryCode: string;
  advisoryName: string;
  products: { code: string; name: string }[];
  createdAt: string | null;
  invitedAt: string | null;
  inviteExpiresAt: string | null;
  inviteExpired: boolean;
  lastLoginAt: string | null;
}

export interface UsersReportFilterOption {
  code: string;
  name: string;
}

export interface UsersReportResponse {
  data: UsersReportRow[];
  meta: UsersResponse['meta'];
  summary: {
    total: number;
    firstAccess: number;
    active: number;
    inactive: number;
    expiredInvites: number;
  };
  filterOptions: {
    /** `advisoryCode`: assessoria vinculada à corretora (vazio se não houver). */
    brokers: (UsersReportFilterOption & { advisoryCode: string })[];
    advisories: UsersReportFilterOption[];
    products: UsersReportFilterOption[];
  };
  generatedAt: string;
}

export interface UsersReportFilters {
  search?: string;
  /** Status separados por vírgula. */
  statuses?: string;
  scope?: string;
  groupName?: string;
  brokerCode?: string;
  advisoryCode?: string;
  productCode?: string;
  invitedFrom?: string;
  invitedTo?: string;
  lastLoginFrom?: string;
  lastLoginTo?: string;
  /** 'true' | 'false' | '' */
  inviteExpired?: string;
  sortField?: string;
  sortType?: 'asc' | 'desc';
}

export interface GetUsersReportParams extends UsersReportFilters {
  page?: number;
  pageSize?: number;
}

const buildUsersReportQuery = (params: GetUsersReportParams) => {
  const { page, pageSize, ...filters } = params;
  const query = new URLSearchParams();
  if (page) query.append('pageNumber', String(page));
  if (pageSize) query.append('pageSize', String(pageSize));
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  return query.toString();
};

// Formato de resposta do portal-bff
export interface UsersResponse {
  data: User[];
  meta: {
    pageNumber: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Mensagem de `errors.email` de um ProblemDetails de validação — lida de um
 * clone, para o corpo continuar disponível ao `handleJsonResponse`.
 */
async function readEmailFieldError(
  response: Response
): Promise<string | undefined> {
  try {
    const body = await response.clone().json();
    const messages: unknown = body?.errors?.email;
    const list = Array.isArray(messages) ? messages : [messages];
    const text = list
      .filter((m): m is string => typeof m === 'string' && m.trim() !== '')
      .join('; ');
    return text || undefined;
  } catch {
    return undefined;
  }
}

export class UsersService extends BaseService {
  constructor() {
    super(
      import.meta.env.VITE_AUTH_API_URL ||
      'https://u4x8665l4d.execute-api.us-east-1.amazonaws.com/development'
    );
  }

  async getUsers(
    params: GetUsersParams = {}
  ): Promise<{ success: boolean; data?: UsersResponse; error?: string }> {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('pageNumber', params.page.toString());
      if (params.limit) queryParams.append('pageSize', params.limit.toString());
      if (params.search) queryParams.append('filter', params.search);
      if (params.role) queryParams.append('groupName', params.role);
      if (params.partyCode) queryParams.append('partyCode', params.partyCode);

      const queryString = queryParams.toString();
      const path = `/portal/v1/identity/users${queryString ? `?${queryString}` : ''}`;
      const response = await this.get(path, {}, import.meta.env.VITE_API_URL);
      return await this.handleJsonResponse<UsersResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Relatório de usuários (seguradora): preview paginado, totais e opções de filtro. */
  async getUsersReport(
    params: GetUsersReportParams = {}
  ): Promise<{ success: boolean; data?: UsersReportResponse; error?: string }> {
    try {
      const query = buildUsersReportQuery(params);
      const response = await this.get(
        `/portal/v1/identity/users/report${query ? `?${query}` : ''}`,
        {},
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<UsersReportResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /** Baixa o .xlsx do relatório de usuários com os filtros atuais (resposta bruta = blob). */
  async exportUsersReport(filters: UsersReportFilters = {}): Promise<Response> {
    const query = buildUsersReportQuery(filters);
    return this.get(
      `/portal/v1/identity/users/report/export${query ? `?${query}` : ''}`,
      {},
      import.meta.env.VITE_API_URL
    );
  }

  async getUserById(
    userId: string
  ): Promise<{ success: boolean; data?: User; error?: string }> {
    try {
      const response = await this.get(
        `/portal/v1/identity/users/${userId}`,
        {},
        import.meta.env.VITE_API_URL
      ); //enddpoint ainda nao existe no backend
      return await this.handleJsonResponse<User>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async registerUser(payload: RegisterUserPayload): Promise<{
    success: boolean;
    data?: User;
    error?: string;
    message?: string;
  }> {
    try {
      const response = await this.post(
        '/portal/v1/identity/users',
        payload,
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse<User>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async updateUser(payload: EditUserPayload): Promise<{
    success: boolean;
    data?: User;
    error?: string;
    /** 409: o e-mail pedido já é o login de outro usuário. */
    emailInUse?: boolean;
    /** 400 com `errors.email`: o e-mail pedido foi recusado (ex.: caractere). */
    emailError?: string;
  }> {
    try {
      const response = await this.put(
        '/portal/v1/identity/users',
        payload,
        import.meta.env.VITE_API_URL
      );
      const emailError =
        response.status === 400
          ? await readEmailFieldError(response)
          : undefined;
      const result = await this.handleJsonResponse<User>(response);
      if (response.status === 409) return { ...result, emailInUse: true };
      return emailError ? { ...result, emailError } : result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async deleteUser(
    userId: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await this.delete(
        `/portal/v1/identity/users/${userId}`,
        {},
        import.meta.env.VITE_API_URL
      ); //enddpoint ainda nao existe no backend
      return await this.handleJsonResponse(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async deactivateUser(
    email: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await this.post(
        '/portal/v1/identity/users/deactivate',
        { email },
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async activateUser(
    email: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await this.post(
        '/portal/v1/identity/users/activate',
        { email },
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async resendFirstAccessInvitation(email: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    alreadyCompleted?: boolean;
  }> {
    try {
      const response = await this.post(
        '/portal/v1/identity/auth/resend-first-access',
        { email },
        import.meta.env.VITE_API_URL
      );
      const result = await this.handleJsonResponse(response);
      // 409: o usuário já definiu a senha, não há convite a reenviar.
      return response.status === 409
        ? { ...result, alreadyCompleted: true }
        : result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async adminResetPassword(
    email: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await this.post(
        '/portal/v1/identity/auth/admin-reset-password',
        { email },
        import.meta.env.VITE_API_URL
      );
      return await this.handleJsonResponse(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }
}

export const usersService = new UsersService();
